import { useState, useEffect } from 'react'

type Theme = 'light' | 'dark'
type ThemeMode = Theme | 'system'

const getSystemTheme = (): Theme => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return 'light'
}

export const useTheme = () => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    const userSetMode = localStorage.getItem('theme-mode-user-set') === 'true'
    const savedMode = localStorage.getItem('theme-mode')
    if (userSetMode && (savedMode === 'light' || savedMode === 'dark' || savedMode === 'system')) {
      return savedMode
    }

    return 'system'
  })

  const [theme, setTheme] = useState<Theme>(() => {
    return getSystemTheme()
  })

  useEffect(() => {
    const resolvedTheme = mode === 'system' ? getSystemTheme() : mode
    setTheme(resolvedTheme)

    localStorage.setItem('theme-mode', mode)
    localStorage.setItem('theme', resolvedTheme)
    if (mode === 'system') localStorage.removeItem('theme-user-set')
    else localStorage.setItem('theme-user-set', 'true')

    document.documentElement.classList.remove('light', 'dark')
    document.documentElement.classList.add(resolvedTheme)
  }, [mode])

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = (e: MediaQueryListEvent) => {
      const currentMode = localStorage.getItem('theme-mode')
      if (currentMode === 'system' || currentMode === null) {
        const nextTheme = e.matches ? 'dark' : 'light'
        setTheme(nextTheme)
        document.documentElement.classList.remove('light', 'dark')
        document.documentElement.classList.add(nextTheme)
        localStorage.setItem('theme', nextTheme)
      }
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  const setThemeMode = (nextMode: ThemeMode) => {
    localStorage.setItem('theme-mode-user-set', 'true')
    setMode(nextMode)
  }

  const toggleTheme = () => {
    localStorage.setItem('theme-mode-user-set', 'true')
    setMode(prevMode => {
      if (prevMode === 'system') return 'light'
      if (prevMode === 'light') return 'dark'
      return 'system'
    })
  }

  const resetToSystem = () => {
    localStorage.setItem('theme-mode-user-set', 'true')
    setMode('system')
  }

  return { theme, mode, setThemeMode, toggleTheme, resetToSystem }
}
